import { Box, Button, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AffectGrid } from "../questionnaireComponents/AffectGrid";
import { DummyQuestion } from "../questionnaireComponents/DummyQuestion";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { putDairyAnswerData } from "../utility/putDairyAnswerData";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { AnimatePresence, motion } from "framer-motion";
import { EventValueQuestion } from "../questionnaireComponents/EventValueQuestion";
import { ChatSatisfactionQuestion } from "../questionnaireComponents/ChatSatisfactionQuestion";
import { ChatNoveltyQuestion } from "../questionnaireComponents/ChatNoveltyQuestion";
import { useAuth } from "../../context/AuthContext";
import {
  defaultPanasAnswer,
  Panas,
  panasAnsewerType,
} from "../questionnaireComponents/Panas";
import {
  defaultGodSpeedAnswer,
  GodSpeed,
  godSpeedType,
} from "../utility/GodSpeed";
import { putFirstDayAnswer } from "../utility/putPanasAnswer";
import { getFirstDayAnswerById } from "../utility/getPanasData";

type affectValueType = {
  pleasantness: number;
  arousal: number;
};

let maxPageNumber = 0;

const variants = {
  enter: (dir: "left" | "right") => ({
    x: dir === "right" ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { x: "0%", opacity: 1 },
  exit: (dir: "left" | "right") => ({
    x: dir === "right" ? "-100%" : "100%",
    opacity: 0,
  }),
};

// 項目が変わらない場合はPreと共通化する
export const DebugPostPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  const { idToken, refreshAuthSession } = useAuth();
  const [affectValue, setAffectValue] = useState<affectValueType | null>(null);
  const [dummyValue, setDummyValue] = useState<number | null>(null);
  const [eventValue, setEventValue] = useState<number | null>(null);
  const [satisfactionValue, setSatisfactionValue] = useState<number | null>(
    null
  );
  const [noveltyValue, setNoveltyValue] = useState<number | null>(null);

  const [completeAnswer, setCompleteAnswer] = useState<boolean>(false);

  const [panasAnswer, setPanasAnswer] =
    useState<panasAnsewerType>(defaultPanasAnswer);
  const [godSpeedAnswer, setGodSpeedAnswer] = useState<godSpeedType>(
    defaultGodSpeedAnswer
  );

  const [includePanas, setIncludePanas] = useState<boolean | null>(null);

  const [questionnairePage, setQuestionnairePage] = useState<number>(1);
  const directionRef = useRef<"left" | "right">("right");

  const isAnswerEnable =
    affectValue &&
    eventValue &&
    satisfactionValue &&
    noveltyValue &&
    (includePanas ? isAllNotNull(panasAnswer) : true) &&
    (includePanas ? isAllNotNull(godSpeedAnswer) : true);

  const alertMessageIsOn =
    (!includePanas && !isAnswerEnable && affectValue) ||
    (includePanas && !isAnswerEnable && isAllNotNull(godSpeedAnswer));

  // PANASの回答が完了しているかどうかを判定する関数
  function isAllNotNull(obj: {
    [key: string]: number | null;
  }): obj is { [key: string]: number } {
    return Object.values(obj).every((v): v is number => v !== null);
  }

  const answerButtonClicked = () => {
    // アンケートデータ保存処理
    if (!isAnswerEnable) return;
    if (!idToken) return;

    const data = {
      affectGrid: affectValue,
      eventValue: eventValue,
      satisfactionValue: satisfactionValue,
      noveltyValue: noveltyValue,
    };

    setCompleteAnswer(true);
  };

  const switchTab = (next: number) => {
    const dir = next > questionnairePage ? "right" : "left";
    directionRef.current = dir;
    setQuestionnairePage(next);
  };

  // 最初の読み込み 同日のPre-PANASがあるかどうかでアンケート内容が変わる
  useEffect(() => {
    // if (idToken && user.userId) {
    //   getFirstDayAnswerById(user.userId, idToken).then((data) => {
    //     if (data[0].date === state.date + "-pre") {
    //       maxPageNumber = 6;
    //       setIncludePanas(true);
    //     } else {
    //       maxPageNumber = 4;
    //       setIncludePanas(false);
    //     }
    //   });
    // }
  }, []);

  const firstDayButton = () => {
    maxPageNumber = 6;
    setIncludePanas(true);
  };

  const secondDayButton = () => {
    maxPageNumber = 4;
    setIncludePanas(false);
  };

  if (completeAnswer) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          回答が完了しました。
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          この回答結果は保存されません。
        </Typography>

        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 2,
            backgroundColor: "#f9f9f9",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            回答データ：
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Affect Grid：</strong>
            {affectValue
              ? `pleasant: ${affectValue.pleasantness}, arousal: ${affectValue.arousal}`
              : "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>出来事の価値：</strong> {eventValue ?? "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>チャット満足度：</strong> {satisfactionValue ?? "未回答"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>チャット新規性：</strong> {noveltyValue ?? "未回答"}
          </Typography>

          {includePanas && (
            <>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>PANAS：</strong>
                </Typography>
                <Box sx={{ ml: 2 }}>
                  {Object.entries(panasAnswer).map(([key, value]) => (
                    <Typography key={key} variant="caption" display="block">
                      {key}: {value ?? "未回答"}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>GodSpeed：</strong>
                </Typography>
                <Box sx={{ ml: 2 }}>
                  {Object.entries(godSpeedAnswer).map(([key, value]) => (
                    <Typography key={key} variant="caption" display="block">
                      {key}: {value ?? "未回答"}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Button variant="outlined" onClick={() => navigate("/")}>
          戻る
        </Button>
        <div style={{ height: "2rem" }} />
      </Box>
    );
  }

  if (includePanas === null)
    return (
      <Box
        sx={{
          textAlign: "center",
          marginBottom: "1rem",
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Button color="primary" onClick={firstDayButton}>
          初日アンケートを確認
        </Button>
        <Button color="primary" onClick={secondDayButton}>
          2日目以降のアンケートを確認
        </Button>
      </Box>
    );

  return (
    <>
      <Box sx={{ minHeight: "60%" }}>
        <Box sx={{ textAlign: "center", marginBottom: "1rem" }}>
          <Typography variant="h6">
            事後アンケート({questionnairePage}/{maxPageNumber})
          </Typography>
          <Typography variant="caption">
            チャット後のアンケートにご回答ください。
          </Typography>
        </Box>

        <Box sx={pageButtonContainerStyle}>
          <Button
            onClick={() => setQuestionnairePage((prev) => prev - 1)}
            disabled={questionnairePage === 1}
          >
            <ArrowBackIosIcon />
            前の設問へ
          </Button>
          <Button
            onClick={() => setQuestionnairePage((prev) => prev + 1)}
            disabled={questionnairePage === maxPageNumber}
          >
            次の設問へ
            <ArrowForwardIosIcon />
          </Button>
        </Box>

        <AnimatePresence mode="wait">
          <motion.div
            key={questionnairePage}
            custom={directionRef.current}
            variants={variants}
            initial="enter"
            animate="center"
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              bounce: 0, // ← ここを追加
              duration: 0.5,
            }}
          >
            {questionnairePage === 1 && (
              <EventValueQuestion
                eventValue={eventValue}
                setEventValue={setEventValue}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}
            {questionnairePage === 2 && (
              <ChatSatisfactionQuestion
                satisfactionValue={satisfactionValue}
                setSatisfactionValue={setSatisfactionValue}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}
            {questionnairePage === 3 && (
              <ChatNoveltyQuestion
                noveltyValue={noveltyValue}
                setNoveltyValue={setNoveltyValue}
                isAutoPageTurn={true}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
              />
            )}
            {questionnairePage === 4 && (
              <AffectGrid
                affectValue={affectValue}
                setAffectValue={setAffectValue}
                isAutoPageTurn={includePanas}
                questionnairePage={questionnairePage}
                switchTab={switchTab}
                widthValue={window.innerWidth}
              />
            )}
            {questionnairePage === 5 && (
              <Panas
                panasAnswer={panasAnswer}
                setPanasAnswer={setPanasAnswer}
              />
            )}
            {questionnairePage === 6 && (
              <GodSpeed
                godSpeedAnswer={godSpeedAnswer}
                setGodSpeedAnswer={setGodSpeedAnswer}
              />
            )}
            {questionnairePage === 100 && (
              <DummyQuestion value={dummyValue} setValue={setDummyValue} />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      <Box sx={{ textAlign: "center", marginTop: "3rem" }}>
        <Button
          variant="contained"
          disabled={!isAnswerEnable}
          style={{ width: "fit-content" }}
          onClick={answerButtonClicked}
        >
          回答する
        </Button>
        {alertMessageIsOn && (
          <Typography sx={{ marginTop: "0.2rem", color: "#C0392B" }}>
            未回答の設問があります！
          </Typography>
        )}
        <div style={{ height: "1rem" }} />
      </Box>
    </>
  );
};

// css
const pageButtonContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  padding: "0.5rem 1rem",
};
